"use client"

import { useEffect, useRef, useState } from "react"
import { ArrowUp, Camera, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { CAPTURE_EXCLUDE_ATTR } from "@/modules/feedback/lib/client/dom-capture"

const POS_KEY = "casper:capture-pill-pos"
const MARGIN = 6
/** Сдвиг пальца, после которого нажатие считается перетаскиванием, а не тапом */
const DRAG_PX = 5
const HINT_MS = 2200

/** Метка для обоих снимков DOM: dom-capture (кнопки в формах) и page-capture (жесты в мобильном приложении) */
export const excludeFromCapture = { [CAPTURE_EXCLUDE_ATTR]: "", "data-capture-ignore": "" }
/** Пока кнопку тащат, page-capture не снимает кадры — иначе перетаскивание дёргается */
const HOLD_ATTR = "data-capture-hold"

interface Pos {
  x: number
  y: number
}

function clamp(p: Pos, el: HTMLElement | null): Pos {
  const w = el?.offsetWidth ?? 0
  const h = el?.offsetHeight ?? 0
  return {
    x: Math.min(Math.max(MARGIN, p.x), Math.max(MARGIN, window.innerWidth - w - MARGIN)),
    y: Math.min(Math.max(MARGIN, p.y), Math.max(MARGIN, window.innerHeight - h - MARGIN)),
  }
}

function readPos(): Pos | null {
  try {
    const p = JSON.parse(localStorage.getItem(POS_KEY) || "null") as Pos | null
    return p && Number.isFinite(p.x) && Number.isFinite(p.y) ? p : null
  } catch {
    return null
  }
}

/**
 * Маленькая полупрозрачная кнопка поверх приложения (как индикатор записи в iPhone): по умолчанию вверху по центру,
 * перетаскивается мышью и пальцем, позиция общая для записи и скриншота. Тап по кнопкам внутри работает,
 * перетаскивание можно начать с любого места.
 */
export function FloatingPill({ children, className, testId, title, onRect }: { children: React.ReactNode; className?: string; testId?: string; title?: string; onRect?: (r: DOMRect) => void }) {
  const box = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<Pos | null>(null)
  const [dragging, setDragging] = useState(false)
  const moved = useRef(false)
  const onRectRef = useRef(onRect)
  onRectRef.current = onRect

  useEffect(() => {
    const saved = readPos()
    if (saved) setPos(clamp(saved, box.current))
    const onResize = () => setPos((p) => (p ? clamp(p, box.current) : p))
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  useEffect(() => {
    if (box.current) onRectRef.current?.(box.current.getBoundingClientRect())
  }, [pos])

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!box.current || (e.pointerType === "mouse" && e.button !== 0)) return
    const r = box.current.getBoundingClientRect()
    const start = { x: e.clientX, y: e.clientY }
    const offset = { x: e.clientX - r.left, y: e.clientY - r.top }
    const id = e.pointerId
    moved.current = false
    let last: Pos | null = null
    const move = (ev: PointerEvent) => {
      if (ev.pointerId !== id) return
      if (!moved.current && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < DRAG_PX) return
      if (!moved.current) {
        setDragging(true)
        document.documentElement.setAttribute(HOLD_ATTR, "")
      }
      moved.current = true
      last = clamp({ x: ev.clientX - offset.x, y: ev.clientY - offset.y }, box.current)
      setPos(last)
    }
    const up = (ev: PointerEvent) => {
      if (ev.pointerId !== id) return
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
      window.removeEventListener("pointercancel", up)
      setDragging(false)
      document.documentElement.removeAttribute(HOLD_ATTR)
      if (last) localStorage.setItem(POS_KEY, JSON.stringify(last))
    }
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
    window.addEventListener("pointercancel", up)
  }

  return (
    <div
      ref={box}
      {...excludeFromCapture}
      className={cn(
        "pointer-events-auto fixed z-[100] flex h-8 touch-none select-none items-center gap-1 rounded-full bg-black/70 p-1 text-white shadow-lg ring-1 ring-white/15 backdrop-blur-sm transition-opacity",
        dragging ? "cursor-grabbing opacity-100" : "cursor-grab opacity-45 hover:opacity-100 focus-within:opacity-100",
        !pos && "left-1/2 top-[calc(env(safe-area-inset-top)+6px)] -translate-x-1/2",
        className,
      )}
      style={pos ? { left: pos.x, top: pos.y } : undefined}
      onPointerDown={onPointerDown}
      onClickCapture={(e) => {
        if (!moved.current) return
        moved.current = false
        e.preventDefault()
        e.stopPropagation()
      }}
      data-testid={testId}
      role="status"
      title={title}
    >
      {children}
    </div>
  )
}

/** Короткая подсказка с затемнением экрана: стрелка указывает на кнопку, через пару секунд исчезает */
export function CaptureHint({ text, anchor }: { text: string; anchor: DOMRect | null }) {
  const [phase, setPhase] = useState<"in" | "out" | "gone">("in")
  useEffect(() => {
    const out = setTimeout(() => setPhase("out"), HINT_MS)
    const gone = setTimeout(() => setPhase("gone"), HINT_MS + 350)
    return () => {
      clearTimeout(out)
      clearTimeout(gone)
    }
  }, [])
  if (phase === "gone") return null
  const below = !anchor || anchor.top < window.innerHeight / 2
  const cx = anchor ? anchor.left + anchor.width / 2 : window.innerWidth / 2
  const left = Math.min(Math.max(cx, 140), window.innerWidth - 140)
  return (
    <div
      {...excludeFromCapture}
      className={cn("fixed inset-0 z-[99] bg-black/55 transition-opacity duration-300", phase === "out" ? "pointer-events-none opacity-0" : "opacity-100")}
      onPointerDown={() => setPhase("out")}
      data-testid="capture-hint"
    >
      <div
        className={cn("absolute flex w-[260px] -translate-x-1/2 flex-col items-center gap-1.5 text-center text-[15px] font-medium leading-snug text-white drop-shadow", !below && "flex-col-reverse")}
        style={below ? { left, top: (anchor?.bottom ?? 40) + 10 } : { left, bottom: window.innerHeight - (anchor?.top ?? 0) + 10 }}
      >
        <ArrowUp className={cn("h-6 w-6 animate-bounce", !below && "rotate-180")} />
        <span>{text}</span>
      </div>
    </div>
  )
}

/** Кнопка «снять» для скриншота: висит, пока пользователь открывает нужный экран */
export function ScreenshotTrigger({ onShoot, onCancel }: { onShoot: () => void; onCancel: () => void }) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  return (
    <>
      <FloatingPill testId="screenshot-trigger" title="Перетащите, чтобы не мешала" onRect={setAnchor}>
        <button type="button" onClick={onShoot} className="flex h-6 items-center gap-1 rounded-full bg-white px-2.5 text-xs font-semibold text-slate-900" aria-label="Сделать скриншот" data-testid="screenshot-shoot">
          <Camera className="h-3.5 w-3.5" />
          Снять
        </button>
        <button type="button" onClick={onCancel} className="flex h-6 w-6 items-center justify-center rounded-full text-white/70 hover:text-white" aria-label="Отменить скриншот" data-testid="screenshot-cancel">
          <X className="h-3.5 w-3.5" />
        </button>
      </FloatingPill>
      {anchor && <CaptureHint text="Откройте нужный экран. Как только будете готовы, нажмите на эту кнопку" anchor={anchor} />}
    </>
  )
}
